import { useCallback, type ReactNode } from 'react';

import { StatCard } from '@/shared/ui/stat-card';
import { dashboardApi } from '@/modules/dashboard/services/api';
import { DashboardWidgetFrame, type DashboardWidgetProps } from './widget-frame';

export type StockSummaryWidgetProps = DashboardWidgetProps & {
  /**
   * Whether the actor may read the tenant's offers.
   *
   * The SKU lives on the OFFER, and a stock line is keyed by the offer id — so a line can only
   * be NAMED by someone who may read the offers. The page knows the actor's grants and says so
   * here, rather than letting the widget ask the server for something it would refuse.
   */
  readonly canReadOffers: boolean;
};

/**
 * The tenant's STOCK.
 *
 * ---------------------------------------------------------------------------
 * THE THRESHOLD IS PRINTED, BECAUSE IT IS THE CONSOLE'S OWN RULE
 * ---------------------------------------------------------------------------
 * The platform has no reorder-point concept: nothing in the API says what "too little" is. So
 * the low-stock figure is produced by a CONSOLE policy — and the widget says so, in the caption
 * of the card that carries the number. A reader who sees "1 line at or below 10 units" can see
 * the rule that produced it, and can ask for it to change. A number with an unstated rule is a
 * number nobody can argue with.
 *
 * ---------------------------------------------------------------------------
 * A LINE IS NAMED WHEN THE NAME IS THE ACTOR'S TO READ
 * ---------------------------------------------------------------------------
 * A low-stock line is identified by its SKU where the actor may read the offers, and by the
 * offer id where they may not. Both are the API's own identifier — the widget invents neither.
 */
export function StockSummaryWidget({
  className,
  canReadOffers,
}: StockSummaryWidgetProps): ReactNode {
  const load = useCallback(
    () => dashboardApi.stock({ includeSkus: canReadOffers }),
    [canReadOffers],
  );

  return (
    <DashboardWidgetFrame
      title="Stock"
      load={load}
      className={className}
      isEmpty={(data) => data.lines === 0}
      emptyTitle="No stock tracked yet"
      emptyDescription="Stock appears once an offer is stocked at a location."
    >
      {(data) => (
        <div className="flex flex-col gap-3">
          <div className="grid gap-3 sm:grid-cols-3">
            <StatCard
              label="Lines tracked"
              value={String(data.lines)}
              caption="Stock balances across all locations"
            />
            <StatCard label="Units on hand" value={String(data.unitsOnHand)} />
            <StatCard
              label="Low stock"
              value={String(data.lowStock.length)}
              tone={data.lowStock.length === 0 ? 'positive' : 'warning'}
              caption={`At or below ${data.lowStockThresholdUnits} units — a console rule`}
            />
          </div>

          {data.lowStock.length === 0 ? null : (
            <ul className="flex flex-col">
              {data.lowStock.map((line) => (
                <li
                  key={`${line.itemId}-${line.locationId}`}
                  className="flex items-center justify-between gap-3 border-b border-border py-2 text-sm last:border-b-0"
                >
                  <span
                    className="truncate font-mono text-xs text-content-muted"
                    title={line.itemId}
                  >
                    {line.sku ?? line.itemId}
                  </span>
                  <span className="text-content">
                    {line.available} {line.unitOfMeasure}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </DashboardWidgetFrame>
  );
}
